import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUniversity, FaCheckCircle, FaTimesCircle, FaMapMarkerAlt, FaTimes, FaRegCopy, FaSyncAlt, FaQuestionCircle, FaChevronDown, FaShieldAlt } from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';

/* ── IBAN'ı yazarken sadece geçerli olabilecek karakterlere izin ver ──
   1. karakter yalnızca 'T', 2. karakter yalnızca 'R', sonrası yalnızca rakam. */
const sanitizeVeGrupla = (raw) => {
  const upper = raw.toUpperCase();
  let temiz = '';
  for (const ch of upper) {
    if (temiz.length >= 26) break;
    if (temiz.length === 0)      { if (ch === 'T') temiz += ch; }
    else if (temiz.length === 1) { if (ch === 'R') temiz += ch; }
    else if (/[0-9]/.test(ch))   { temiz += ch; }
  }
  return temiz;
};
const grupla = (temiz) => temiz.match(/.{1,4}/g)?.join(' ') ?? temiz;

/* ── İstemci tarafı MOD97 (ISO 7064) — ağ isteği atmadan anlık geçersizlik tespiti ── */
const harfCevir = (s) => s.replace(/[A-Z]/g, (ch) => (ch.charCodeAt(0) - 55).toString());
const localMod97Gecerli = (iban26) => {
  const yenidenSirali = iban26.slice(4) + iban26.slice(0, 4);
  // eslint-disable-next-line no-undef -- BigInt tüm modern tarayıcılarda mevcut, CRA'nın eslint env'i tanımıyor
  return (BigInt(harfCevir(yenidenSirali)) % 97n) === 1n;
};

const SEGMENTLER = [
  { key: 'ulkeKodu',       label: 'Ülke',       color: '#64748b' },
  { key: 'kontrolBasamak', label: 'Kontrol',     color: '#a78bfa' },
  { key: 'bankaKodu',      label: 'Banka Kodu',  color: '#22d3ee' },
  { key: 'rezerv',         label: 'Rezerv',      color: '#475569' },
  { key: 'subeKodu',       label: 'Şube Kodu',   color: '#f59e0b' },
  { key: 'hesapNo',        label: 'Hesap No',    color: '#34d399' },
];
const segmentGrubu = { hidden: {}, show: { transition: { staggerChildren: 0.045 } } };
const segmentItem  = {
  hidden: { opacity: 0, y: 8, scale: .95 },
  show:   { opacity: 1, y: 0, scale: 1, transition: { duration: .28, ease: [0.16, 1, 0.3, 1] } },
};

const IbanKontrol = () => {
  const [input,      setInput]      = useState('');
  const [durum,       setDurum]      = useState('bos'); // bos | yaziyor | kontrol-ediliyor | gecerli | gecersiz
  const [sonuc,       setSonuc]      = useState(null);
  const [detayAcik,   setDetayAcik]  = useState(false);
  const gecersizMesaj = useRef('Bu IBAN geçersiz (MOD97 doğrulaması başarısız).');

  const temiz = input.replace(/\s/g, '');

  // Yazarken otomatik kontrol: 26 haneye ulaşınca istemci MOD97'si + (geçerliyse) sunucu sorgusu
  useEffect(() => {
    if (temiz.length < 26) {
      setSonuc(null);
      setDetayAcik(false);
      setDurum(temiz.length === 0 ? 'bos' : 'yaziyor');
      return;
    }

    if (!localMod97Gecerli(temiz)) {
      gecersizMesaj.current = 'Bu IBAN geçersiz (MOD97 doğrulaması başarısız).';
      setSonuc(null);
      setDurum('gecersiz');
      return;
    }

    let iptalEdildi = false;
    setDurum('kontrol-ediliyor');

    (async () => {
      try {
        const r = await axiosInstance.get('/iban/check', { params: { iban: temiz } });
        if (iptalEdildi) return;
        if (r.data.formatGecerli && r.data.gecerli) {
          setSonuc(r.data);
          setDetayAcik(false);
          setDurum('gecerli');
        } else {
          gecersizMesaj.current = r.data.hata || 'Bu IBAN geçersiz.';
          setSonuc(null);
          setDurum('gecersiz');
        }
      } catch {
        if (iptalEdildi) return;
        toast.error('IBAN kontrol edilemedi, sunucuya ulaşılamıyor.');
        setDurum('yaziyor');
      }
    })();

    return () => { iptalEdildi = true; };
  }, [temiz]);

  const handleChange = (e) => setInput(grupla(sanitizeVeGrupla(e.target.value)));
  const handleTemizle = () => { setInput(''); setSonuc(null); setDurum('bos'); };
  const handleKopyala = () => { navigator.clipboard.writeText(temiz); toast.success('IBAN kopyalandı.'); };

  const renkler = {
    bos:               { border: 'rgba(255,255,255,.08)', glow: 'transparent', bar: 'rgba(255,255,255,.12)' },
    yaziyor:           { border: 'rgba(255,255,255,.1)',  glow: 'transparent', bar: '#22d3ee' },
    'kontrol-ediliyor': { border: 'rgba(34,211,238,.5)',   glow: 'rgba(34,211,238,.14)', bar: '#22d3ee' },
    gecerli:           { border: 'rgba(16,185,129,.5)',   glow: 'rgba(16,185,129,.14)', bar: '#10b981' },
    gecersiz:          { border: 'rgba(239,68,68,.5)',    glow: 'rgba(239,68,68,.14)',  bar: '#ef4444' },
  }[durum];

  const subeMesaji = () => {
    if (!sonuc?.sube) return null;
    if (sonuc.sube.ad) return { text: sonuc.sube.ad, dim: false };
    if (!sonuc.subeVeriHazir) return { text: 'Şube verisi henüz yüklenmedi.', dim: true, belirsiz: true };
    if (sonuc.sube.belirsiz) return { text: `IBAN'dan kesin şube tespiti yapılamıyor (${sonuc.sube.adaySayisi} olası şube)`, dim: true, belirsiz: true };
    return { text: 'Bu şube koduyla eşleşme bulunamadı.', dim: true, belirsiz: true };
  };
  const subeInfo = subeMesaji();

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(34,211,238,.06), transparent), #090a0c', padding: '40px 24px', color: '#e2e8f0', fontFamily: "'Outfit','Inter',sans-serif", boxSizing: 'border-box' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=DM+Mono:wght@500&display=swap');
        *{box-sizing:border-box}
        .ik-inp{caret-color:#22d3ee}
        .ik-inp::placeholder{color:rgba(255,255,255,.14);letter-spacing:1.5px}
        .ik-iconbtn{transition:all .15s}
        .ik-iconbtn:hover{background:rgba(255,255,255,.08)!important;color:#f1f5f9!important}
        @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        .ik-spin{animation:spin .9s linear infinite}
        @keyframes shake{10%,90%{transform:translateX(-1px)}20%,80%{transform:translateX(2px)}30%,50%,70%{transform:translateX(-4px)}40%,60%{transform:translateX(4px)}}
        .ik-shake{animation:shake .4s}
      `}</style>

      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35, ease: 'easeOut' }}
          style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}
        >
          <div style={{ width: 46, height: 46, borderRadius: 14, background: 'linear-gradient(135deg,rgba(34,211,238,.18),rgba(8,145,178,.06))', border: '1px solid rgba(34,211,238,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22d3ee', fontSize: '1.15rem', boxShadow: '0 0 22px rgba(34,211,238,.15)' }}>
            <FaUniversity />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#f8fafc', letterSpacing: '-.4px' }}>IBAN Kontrol</h2>
            <p style={{ margin: '3px 0 0', fontSize: '.72rem', color: 'rgba(255,255,255,.25)' }}>Yazdıkça anlık doğrulama · banka &amp; şube tespiti</p>
          </div>
        </motion.div>

        {/* Input kartı */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35, delay: .05, ease: 'easeOut' }}
          style={{ background: '#111318', border: '1px solid rgba(255,255,255,.06)', borderRadius: 20, padding: 24, marginBottom: 16 }}
        >
          <div style={{ position: 'relative' }}>
            <input
              className={`ik-inp${durum === 'gecersiz' ? ' ik-shake' : ''}`}
              type="text"
              value={grupla(temiz)}
              onChange={handleChange}
              placeholder="TR__ ____ ____ ____ ____ ____ __"
              autoFocus
              style={{
                width: '100%', padding: '16px 90px 16px 18px', background: '#0d0e12',
                border: `1.5px solid ${renkler.border}`, borderRadius: 14, color: '#f8fafc',
                fontSize: '1.15rem', fontFamily: "'DM Mono', 'Outfit', monospace", letterSpacing: '2px',
                outline: 'none', transition: 'border-color .25s, box-shadow .25s',
                boxShadow: renkler.glow !== 'transparent' ? `0 0 0 5px ${renkler.glow}` : 'none',
              }}
            />
            <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 4 }}>
              {durum === 'kontrol-ediliyor' && <FaSyncAlt className="ik-spin" style={{ fontSize: '.85rem', color: '#22d3ee', marginRight: 4 }} />}
              {durum === 'gecerli' && <FaCheckCircle style={{ fontSize: '.9rem', color: '#10b981', marginRight: 4 }} />}
              {durum === 'gecersiz' && <FaTimesCircle style={{ fontSize: '.9rem', color: '#ef4444', marginRight: 4 }} />}
              {temiz && (
                <>
                  <button className="ik-iconbtn" onClick={handleKopyala} title="Kopyala" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: 'rgba(255,255,255,.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaRegCopy style={{ fontSize: '.76rem' }} />
                  </button>
                  <button className="ik-iconbtn" onClick={handleTemizle} title="Temizle" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: 'rgba(255,255,255,.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaTimes style={{ fontSize: '.76rem' }} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* İlerleme çubuğu */}
          <div style={{ height: 3, background: 'rgba(255,255,255,.05)', borderRadius: 3, marginTop: 12, overflow: 'hidden' }}>
            <motion.div
              animate={{ width: `${Math.min(temiz.length / 26, 1) * 100}%`, backgroundColor: renkler.bar }}
              transition={{ duration: .25, ease: 'easeOut' }}
              style={{ height: '100%', borderRadius: 3 }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
            <span style={{ fontSize: '.64rem', color: 'rgba(255,255,255,.2)' }}>
              {durum === 'kontrol-ediliyor' ? 'Banka ve şube bilgisi sorgulanıyor…' : durum === 'bos' ? 'TR ile başlayan 26 haneli IBAN girin' : ''}
            </span>
            <span style={{ fontSize: '.64rem', fontFamily: "'DM Mono',monospace", color: temiz.length === 26 ? renkler.bar : 'rgba(255,255,255,.2)' }}>{temiz.length}/26</span>
          </div>
        </motion.div>

        {/* Geçersiz — kompakt satır içi uyarı (kart YOK) */}
        <AnimatePresence>
          {durum === 'gecersiz' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              transition={{ duration: .22 }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', borderRadius: 14, background: 'rgba(239,68,68,.07)', border: '1px solid rgba(239,68,68,.18)', marginBottom: 16, overflow: 'hidden' }}
            >
              <FaTimesCircle style={{ color: '#ef4444', fontSize: '1rem', flexShrink: 0 }} />
              <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#f87171' }}>{gecersizMesaj.current}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Geçerli — fintech "sanal kart" görünümü */}
        <AnimatePresence>
          {durum === 'gecerli' && sonuc && (
            <motion.div
              key={sonuc.iban}
              initial={{ opacity: 0, y: 16, scale: .97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: .98 }}
              transition={{ duration: .35, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Kart */}
              <div style={{
                position: 'relative', overflow: 'hidden', borderRadius: 22, padding: 26,
                background: 'linear-gradient(135deg, #0e3a41 0%, #0a2830 45%, #071b21 100%)',
                border: '1px solid rgba(34,211,238,.22)',
                boxShadow: '0 24px 60px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.05)',
              }}>
                <div style={{ position: 'absolute', top: -90, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,211,238,.14), transparent 70%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: -80, left: -60, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,.08), transparent 70%)', pointerEvents: 'none' }} />

                {/* Üst satır: banka + doğrulandı rozeti */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e2e8f0' }}>
                      <FaUniversity style={{ fontSize: '.85rem' }} />
                    </div>
                    <div style={{ fontSize: '.86rem', fontWeight: 700, color: sonuc.banka ? '#f8fafc' : 'rgba(255,255,255,.4)' }}>
                      {sonuc.banka ? sonuc.banka.ad : 'Bilinmeyen Banka Kodu'}
                    </div>
                  </div>
                  <motion.div
                    initial={{ scale: .5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 380, damping: 18, delay: .1 }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 20, background: 'rgba(16,185,129,.14)', border: '1px solid rgba(16,185,129,.3)' }}
                  >
                    <FaShieldAlt style={{ fontSize: '.65rem', color: '#34d399' }} />
                    <span style={{ fontSize: '.63rem', fontWeight: 700, color: '#34d399', letterSpacing: '.3px' }}>DOĞRULANDI</span>
                  </motion.div>
                </div>

                {/* Orta: büyük IBAN */}
                <div style={{ margin: '26px 0 22px', position: 'relative' }}>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '1.32rem', fontWeight: 500, letterSpacing: '2.5px', color: '#f8fafc', textShadow: '0 2px 12px rgba(0,0,0,.3)', wordBreak: 'break-all' }}>
                    {grupla(sonuc.iban)}
                  </div>
                </div>

                <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.12), transparent)', marginBottom: 18 }} />

                {/* Alt satır: şube */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, position: 'relative' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'rgba(255,255,255,.35)', marginBottom: 5 }}>
                      <FaMapMarkerAlt style={{ fontSize: '.6rem' }} /> Şube
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      {subeInfo?.belirsiz && <FaQuestionCircle style={{ fontSize: '.65rem', color: 'rgba(255,255,255,.25)', marginTop: 3, flexShrink: 0 }} />}
                      <span style={{ fontSize: '.83rem', fontWeight: 600, color: subeInfo?.dim ? 'rgba(255,255,255,.4)' : '#e2e8f0', lineHeight: 1.35 }}>
                        {subeInfo?.text}
                      </span>
                    </div>
                  </div>
                  {sonuc.sube?.kod && (
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'rgba(255,255,255,.35)', marginBottom: 5 }}>Kod</div>
                      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '.83rem', fontWeight: 600, color: '#e2e8f0' }}>{sonuc.sube.kod}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hane dökümü — açılır/kapanır detay */}
              <div style={{ marginTop: 10 }}>
                <button onClick={() => setDetayAcik((o) => !o)} style={{
                  display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,.3)', fontSize: '.73rem', fontWeight: 600, fontFamily: 'inherit', padding: '8px 4px',
                }}>
                  <motion.span animate={{ rotate: detayAcik ? 180 : 0 }} transition={{ duration: .2 }} style={{ display: 'flex' }}>
                    <FaChevronDown style={{ fontSize: '.6rem' }} />
                  </motion.span>
                  Hane dökümünü {detayAcik ? 'gizle' : 'göster'}
                </button>
                <AnimatePresence initial={false}>
                  {detayAcik && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: .25, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: 'hidden' }}
                    >
                      <motion.div variants={segmentGrubu} initial="hidden" animate="show" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 4, paddingBottom: 4 }}>
                        {SEGMENTLER.map((seg) => (
                          <motion.div key={seg.key} variants={segmentItem} style={{ padding: '8px 12px', borderRadius: 9, background: `${seg.color}18`, border: `1px solid ${seg.color}40` }}>
                            <div style={{ fontSize: '.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: seg.color, marginBottom: 2 }}>{seg.label}</div>
                            <div style={{ fontSize: '.83rem', fontWeight: 600, fontFamily: "'DM Mono',monospace", color: '#f1f5f9' }}>{sonuc.parcalar?.[seg.key]}</div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default IbanKontrol;
